import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { AdminShell } from "../../components/admin/AdminShell";
import { StatusBadge } from "../../components/admin/StatusBadge";
import { Button } from "../../components/Button";
import { questions } from "../../config/questions";
import { apiJson } from "../../lib/adminApi";
import type { SubmissionListItem } from "../../types/admin";

type Filters = { search: string; status: string; intent: string; dateFrom: string; dateTo: string; page: number };
const initialFilters: Filters = { search: "", status: "", intent: "", dateFrom: "", dateTo: "", page: 1 };
type ListResult = { items: SubmissionListItem[]; page: number; pageSize: number; total: number; totalPages: number };

function dateTime(value: string) { return new Intl.DateTimeFormat("en-AU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
function primaryFriction(id: string) { return questions[0].options.find((option) => option.id === id)?.label ?? id; }

export function AdminSubmissionsPage() {
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [result, setResult] = useState<ListResult | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const reduceMotion = useReducedMotion();
  const stateTransition = { duration: reduceMotion ? 0 : 0.16, ease: [0.22, 1, 0.36, 1] as const };
  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value !== "") params.set(key, String(value)); });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    setState("loading");
    apiJson<ListResult>(`/api/admin/submissions?${query}`).then((data) => { setResult(data); setState("ready"); }).catch(() => setState("error"));
  }, [query]);

  const apply = (event: React.FormEvent) => { event.preventDefault(); setFilters({ ...draft, page: 1 }); };
  const clear = () => { setDraft(initialFilters); setFilters(initialFilters); };
  const exportHref = `/api/admin/submissions/export.csv?${query}`;
  const toolbarStatus = state === "loading"
    ? "Loading submissions"
    : state === "error"
      ? "Submissions unavailable"
      : result
        ? `${result.total} submission${result.total === 1 ? "" : "s"}`
        : "";

  return (
    <AdminShell title="Friction Snapshot submissions">
      <form className="admin-filters" onSubmit={apply} aria-label="Submission filters">
        <div className="field filter-search">
          <label htmlFor="submission-search">Search</label>
          <input id="submission-search" type="search" placeholder="Name, business or email" value={draft.search} onChange={(event) => setDraft({ ...draft, search: event.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="status-filter">Status</label>
          <select id="status-filter" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
            <option value="">All statuses</option><option>New</option><option>Reviewing</option><option>Contacted</option><option>Closed</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="intent-filter">Intent</label>
          <select id="intent-filter" value={draft.intent} onChange={(event) => setDraft({ ...draft, intent: event.target.value })}>
            <option value="">All intents</option><option>Fixing Today</option><option>Planning for Tomorrow</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="date-from">Submitted from</label>
          <input id="date-from" type="date" value={draft.dateFrom} onChange={(event) => setDraft({ ...draft, dateFrom: event.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="date-to">Submitted to</label>
          <input id="date-to" type="date" value={draft.dateTo} onChange={(event) => setDraft({ ...draft, dateTo: event.target.value })} />
        </div>
        <div className="filter-actions"><Button type="submit">Apply filters</Button><Button type="button" variant="secondary" onClick={clear}>Clear filters</Button></div>
      </form>

      <div className="list-toolbar">
        <p aria-live="polite">{toolbarStatus}</p>
        {state === "ready"
          ? <Button asChild variant="secondary"><a href={exportHref}>Export filtered CSV</a></Button>
          : <Button variant="secondary" disabled>Export filtered CSV</Button>}
      </div>
      <section className="admin-list-region" aria-label="Submission results" aria-busy={state === "loading"}>
      <AnimatePresence initial={false}>
      {state === "loading" && (
        <m.div key="loading" className="admin-state admin-loading-state" role="status" initial={reduceMotion ? false : { opacity: 0.75 }} animate={{ opacity: 1 }} exit={reduceMotion ? undefined : { opacity: 0 }} transition={stateTransition}>
          <strong>Loading submissions…</strong>
          <span className="state-help">Retrieving the latest internal records.</span>
          <span className="loading-lines" aria-hidden="true"><i /><i /><i /></span>
        </m.div>
      )}
      {state === "error" && (
        <m.div key="error" className="admin-state error-summary" role="alert" initial={reduceMotion ? false : { opacity: 0.75, y: -3 }} animate={{ opacity: 1, y: 0 }} transition={stateTransition}>
          <strong>Submissions could not be loaded.</strong>
          <span className="state-help">Refresh to try again.</span>
        </m.div>
      )}
      {state === "ready" && result?.items.length === 0 && (
        <m.div key="empty" className="admin-state" initial={reduceMotion ? false : { opacity: 0.75, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={stateTransition}>
          <strong>No submissions match the active filters.</strong>
          <span className="state-help">Clear or adjust the filters to widen the list.</span>
        </m.div>
      )}
      {state === "ready" && !!result?.items.length && (
        <m.div key="results" className="admin-table-wrap" initial={reduceMotion ? false : { opacity: 0.8, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={stateTransition}>
          <table className="admin-table">
            <caption className="sr-only">Friction Snapshot submissions, newest first</caption>
            <thead><tr><th scope="col">Submitted</th><th scope="col">Full name</th><th scope="col">Business</th><th scope="col">Work email</th><th scope="col">Intent</th><th scope="col">Main friction</th><th scope="col">Status</th><th scope="col">Owner</th><th scope="col"><span className="sr-only">Open</span></th></tr></thead>
            <tbody>{result.items.map((item) => (
              <tr key={item.id}>
                <td data-label="Submitted" className="submission-date">{dateTime(item.submittedAt)}</td>
                <td data-label="Full name" className="submission-name"><strong>{item.fullName}</strong></td>
                <td data-label="Business" className="submission-business">{item.businessName}</td>
                <td data-label="Work email" className="submission-email"><a href={`mailto:${item.workEmail}`}>{item.workEmail}</a></td>
                <td data-label="Intent" className="submission-intent">{item.intent}</td>
                <td data-label="Main friction" className="submission-friction">{primaryFriction(item.primaryFrictionId)}</td>
                <td data-label="Status" className="submission-status"><StatusBadge status={item.status} /></td>
                <td data-label="Owner" className="submission-owner"><span className="mobile-field-label">Owner</span>{item.assignedToAdminUser?.displayName ?? "Unassigned"}</td>
                <td className="submission-open"><Link className="row-link" to={`/admin/submissions/${item.id}`}>Open<span className="sr-only"> submission from {item.fullName}</span></Link></td>
              </tr>
            ))}</tbody>
          </table>
        </m.div>
      )}
      </AnimatePresence>
      </section>
      {result && result.totalPages > 1 && (
        <nav className="pagination" aria-label="Submission pages">
          <Button variant="secondary" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Previous</Button>
          <span>Page {result.page} of {result.totalPages}</span>
          <Button variant="secondary" disabled={filters.page >= result.totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</Button>
        </nav>
      )}
    </AdminShell>
  );
}
